import { useViewportRect } from '@src/hooks/useViewportRect';
import { SAFE_ZONE_REGISTRY } from '@src/safe-zones/registry';
import { SAFE_ZONE_Z_INDEX } from '@src/safe-zones/zIndex';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useSafeZoneBrand } from './BrandSlots';
import { SafeZoneOverlayErrorBoundary } from './SafeZoneOverlayErrorBoundary';

import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type {
  SafeZoneSelection,
  SafeZoneVariantKey,
  SafeZoneVariantState,
} from '@src/safe-zones/types';
import type { AspectRatio } from '@src/types.ts/video';

const logger = LogManager.getLogger('safe-zones:SafeZoneOverlay');

// Z-index layering rationale lives in `@src/safe-zones/zIndex.ts`.
const OVERLAY_Z_INDEX = SAFE_ZONE_Z_INDEX.overlay;

interface SafeZoneOverlayProps {
  selection: SafeZoneSelection;
  aspectRatio: AspectRatio | string | undefined;
  rendererElement: HTMLElement | null | undefined;
  variants?: SafeZoneVariantState;
  onToggleVariant?: (key: SafeZoneVariantKey) => void;
  /**
   * Optional bridge that lets overlay chrome mirror the real video — the
   * LinkedIn play button uses this to actually play/pause, the time readout
   * to show real progress, and the scrubber to render real fill (and seek).
   * When omitted, overlays fall back to static placeholders.
   */
  playback?: PlaybackBridge;
}

// Mounted via portal to document.body so the overlay can never be covered
// by a player iframe or any stacking context inside the modal.
//
// `position: fixed` because we measure with `getBoundingClientRect()` which
// returns viewport-space coordinates.
const OverlayRoot = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: ${OVERLAY_Z_INDEX};
  overflow: hidden;
`;

/**
 * Renders the platform safe-zone overlay anchored to the on-screen rect of
 * `rendererElement`. Portaled to `document.body` to defeat any iframe /
 * player stacking context inside the modal — the overlay must paint on top
 * of the Creatomate iframe and Mux player without exception.
 *
 * Renders nothing when `selection === 'none'`, when no registry entry
 * exists, or when the renderer hasn't been measured yet.
 */
export const SafeZoneOverlay: React.FC<SafeZoneOverlayProps> = observer(
  ({
    selection,
    aspectRatio,
    rendererElement,
    variants,
    onToggleVariant,
    playback,
  }) => {
    const rect = useViewportRect(rendererElement);
    const brand = useSafeZoneBrand();

    if (selection === 'none') return null;
    if (!rect) {
      logger.debug('skipping: no measured rect yet', {
        selection,
        rendererPresent: !!rendererElement,
      });
      return null;
    }

    const definition = SAFE_ZONE_REGISTRY[selection];
    if (!definition) {
      logger.warn('skipping: no registry entry for selection', { selection });
      return null;
    }

    const { Component } = definition;

    return createPortal(
      <OverlayRoot
        data-testid="safe-zone-overlay"
        data-selection={selection}
        style={{
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        }}
      >
        <SafeZoneOverlayErrorBoundary selection={selection}>
          <Component
            rect={{ width: rect.width, height: rect.height }}
            aspectRatio={aspectRatio ?? ''}
            brand={brand}
            variants={variants ?? {}}
            onToggleVariant={onToggleVariant}
            playback={playback}
          />
        </SafeZoneOverlayErrorBoundary>
      </OverlayRoot>,
      document.body,
    );
  },
);
